import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { memo } from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../../store";
import SectionContainer from "./../../../../../common/SectionContainer";
import ReceiptControl from "./MerchantReceiptControl";
import { FastField, FormikProps, useFormikContext } from "formik";
import { Data } from "../../../../../store/terminal/types";
import { FormControlLabel } from "@mui/material";
import CustomizedSwitch from "../../../../../common/formElements/Switch";
import { getValueForField } from "../editConfigurationComponent/ConfigurationOptions";
import { get } from "lodash";
import SectionWrapper from "../../../../../common/pageSections/SectionWrapper";
import CustomizedRadio from "../../../../../common/formElements/RadioButton";
import { Receipt } from "../../../../../store/receipt/types";
import OptionalToolTip from "../../../../../../js/common/toolTip/OptionalToolTip";
import MerchantReceiptControl from "./MerchantReceiptControl";
import CustomerReceiptControl from "./CustomerReceiptControl";

interface EditReceiptComponent {
  bulk: boolean;
  isUpdateAllowed?: boolean;
  showHTMLReceipts: boolean;
  setShowHTMLReceipts: (value: any) => void;
  receiptReadAccess: boolean;
  shouldHideField: (fieldName: string) => boolean;
}

const EditReceiptComponent = (props: EditReceiptComponent) => {
  const { bulk, isUpdateAllowed, showHTMLReceipts, setShowHTMLReceipts, shouldHideField } =
    props;

  const receipts = useSelector(
    (state: ApplicationState) => state.receipt?.data
  );

  const formikProps = useFormikContext();
  const { values, setFieldValue } = formikProps as FormikProps<Data>;

  const checkedHtml =
    values &&
    values.attributes &&
    get(values, "attributes.EnableHTMLReceipts") === "Yes";

  const merchantReceiptsNonHTML =
    receipts && receipts.data
      ? receipts.data.filter(
          (data) =>
            data?.attributes?.For === "Merchant" &&
            data?.attributes?.Type !== "HTML"
        )
      : [];

  const customerReceiptsNonHTML =
    receipts && receipts.data
      ? receipts.data.filter(
          (data) =>
            data?.attributes?.For === "Customer" &&
            data?.attributes?.Type !== "HTML"
        )
      : [];

  const merchantReceiptsHTML =
    receipts && receipts.data
      ? receipts.data.filter(
          (data) =>
            data?.attributes?.For === "Merchant" &&
            data?.attributes?.Type === "HTML"
        )
      : [];

  const customerReceiptsHTML =
    receipts && receipts.data
      ? receipts.data.filter(
          (data) =>
            data?.attributes?.For === "Customer" &&
            data?.attributes?.Type === "HTML"
        )
      : [];

  const receiptPrintOptions = [
    { value: "Terminal", label: "Terminal" },
    { value: "POS", label: "POS" },
  ];

  const ReceiptConfigTooltips = {
    PrintTransactionListing:
      "Print transaction listing button will appear on last settlement summary screen when this is turned on. This toggle only applies to the NAB terminal fleet",
  };

  const renderReceiptTemplate = () => {
    if (showHTMLReceipts) {
      return (
        <>
          <Grid item xs={12} md={6}>
            <MerchantReceiptControl
              heading="Merchant Receipts"
              templateAttributeName="attributes.HTMLReceipts"
              statusAttributeName="attributes.MerchantReceiptPrint"
              receipts={merchantReceiptsHTML}
              isUpdateAllowed={isUpdateAllowed}
              showHTMLReceipts={showHTMLReceipts}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomerReceiptControl
              heading="Customer Receipts"
              templateAttributeName="attributes.HTMLReceipts"
              statusAttributeName="attributes.CustomerReceiptPrint"
              receipts={customerReceiptsHTML}
              isUpdateAllowed={isUpdateAllowed}
              showHTMLReceipts={showHTMLReceipts}
            />
          </Grid>
        </>
      );
    }
    return (
      <>
        <Grid item xs={12} md={6}>
          <MerchantReceiptControl
            heading="Merchant Receipts"
            templateAttributeName="attributes.ReceiptMerchant"
            statusAttributeName="attributes.MerchantReceiptPrint"
            receipts={merchantReceiptsNonHTML}
            isUpdateAllowed={isUpdateAllowed}
            showHTMLReceipts={showHTMLReceipts}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomerReceiptControl
            heading="Customer Receipts"
            templateAttributeName="attributes.Receipt"
            statusAttributeName="attributes.CustomerReceiptPrint"
            receipts={customerReceiptsNonHTML}
            isUpdateAllowed={isUpdateAllowed}
            showHTMLReceipts={showHTMLReceipts}
          />
        </Grid>
      </>
    );
  };

  const enableHtmlReceiptsToggle = () => {
    return (
      <>
        <Grid item xs={12} md={6}>
          <SectionWrapper isChild={true} title="Print Options">
            <Grid container spacing={2} xs={12}>
              <Grid item xs={12} md={6}>
                <FastField
                  name={"attributes.ReceiptPrint"}
                  as={(props: FormikProps<Receipt>) => (
                    <FormControl fullWidth>
                      <InputLabel shrink>Receipt Print</InputLabel>
                      <RadioGroup {...props} style={{ paddingTop: "1rem" }}>
                        {receiptPrintOptions.map((option, index) => (
                          <CustomizedRadio
                            value={option.value}
                            label={option.label}
                            key={index}
                            disabled={!isUpdateAllowed}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </SectionWrapper>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionWrapper isChild title="Configuration">
            <Box pl={2} width="100%">
              <Grid container>
                <Grid
                  item
                  xs={12}
                  md={
                    values.attributes.Manufacturer &&
                    values.attributes.Manufacturer.toLocaleLowerCase() ===
                      "quest"
                      ? 8
                      : 12
                  }
                >
                  <Grid xs={12} md={12}>
                    <FormControlLabel
                      label={"Show only HTML receipts"}
                      control={
                        <CustomizedSwitch
                          name={"attributes.EnableHTMLReceipts"}
                          checked={checkedHtml}
                          onChange={(event, value) => {
                            setShowHTMLReceipts(value);
                            setFieldValue(
                              "attributes.EnableHTMLReceipts",
                              getValueForField(
                                "attributes.EnableHTMLReceipts",
                                value
                              )
                            );
                          }}
                          disabled={!isUpdateAllowed}
                        />
                      }
                    />
                  </Grid>
                  <Grid xs={12} md={12}>
                    <FormControlLabel
                      control={
                        <CustomizedSwitch
                          checked={
                            values.attributes
                              .PromptCustomerReceiptFirst as boolean
                          }
                          onChange={(event) => {
                            setFieldValue(
                              "attributes.PromptCustomerReceiptFirst",
                              event.target.checked
                            );
                          }}
                          disabled={!isUpdateAllowed}
                        />
                      }
                      label="Prompt Customer Receipt First"
                    />
                  </Grid>
                  {values.attributes.Manufacturer &&
                    values.attributes.Manufacturer.toLocaleLowerCase() ===
                      "quest" && (
                      <OptionalToolTip
                        tooltip={ReceiptConfigTooltips.PrintTransactionListing}
                      >
                        <FormControlLabel
                          label={"Print transaction listing"}
                          control={
                            <CustomizedSwitch
                              key={`${values.attributes.PrintTransactionListing}`}
                              checked={
                                values.attributes
                                  .PrintTransactionListing as boolean
                              }
                              onChange={(event) =>
                                setFieldValue(
                                  "attributes.PrintTransactionListing",
                                  event.target.checked
                                )
                              }
                              disabled={!isUpdateAllowed}
                            />
                          }
                        />
                      </OptionalToolTip>
                    )}
                </Grid>
              </Grid>
            </Box>
          </SectionWrapper>
        </Grid>
      </>
    );
  };

  return (
    <SectionContainer heading="Receipt" name="sections.receipt" bulk={bulk}>
      <Grid container spacing={2}>
        {renderReceiptTemplate()}
        {enableHtmlReceiptsToggle()}
      </Grid>
    </SectionContainer>
  );
};

export default memo(EditReceiptComponent);
